import React, { useEffect, useState, Component } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, Polyline, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { SupplierData } from '../data/mockData';
import { HazardData } from '../data/mockHazardsData';
// Custom marker icons for different statuses
const getMarkerIcon = (status: string, tier: number) => {
  let color = '#4caf50'; // Default green for 'On Schedule'
  if (status === 'Delayed') {
    color = '#fbc02d'; // Yellow for 'Delayed'
  } else if (status === 'At Risk') {
    color = '#e53935'; // Red for 'At Risk'
  }
  // Size based on tier (tier 1 largest, tier 3 smallest)
  const size = 20 - (tier - 1) * 4;
  return L.divIcon({
    html: `<div style="background-color: ${color}; width: ${size}px; height: ${size}px; border-radius: 50%; border: 2px solid white;"></div>`,
    className: 'custom-div-icon',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
};
// Modern enterprise-looking hazard icons
const getHazardIcon = (type: string, severity: string) => {
  let iconUrl = '';
  let iconSize = [28, 28];
  // Set icon based on hazard type - using more modern, enterprise-suitable icons
  switch (type) {
    case 'natural':
      // Modern weather/storm icon
      iconUrl = 'https://cdn-icons-png.flaticon.com/512/4150/4150904.png';
      break;
    case 'port':
      // Modern shipping/logistics icon
      iconUrl = 'https://cdn-icons-png.flaticon.com/512/9703/9703236.png';
      break;
    case 'political':
      // Modern government/policy icon
      iconUrl = 'https://cdn-icons-png.flaticon.com/512/3575/3575596.png';
      break;
    case 'infrastructure':
      // Modern construction/infrastructure icon
      iconUrl = 'https://cdn-icons-png.flaticon.com/512/3028/3028331.png';
      break;
    default:
      // Generic warning icon
      iconUrl = 'https://cdn-icons-png.flaticon.com/512/4947/4947156.png';
  }
  return L.icon({
    iconUrl,
    iconSize,
    iconAnchor: [14, 14],
    popupAnchor: [0, -14]
  });
};
// Get circle color based on hazard severity
const getHazardCircleColor = (severity: string) => {
  switch (severity) {
    case 'low':
      return '#4caf50';
    case 'medium':
      return '#fbc02d';
    case 'high':
      return '#ff9800';
    case 'critical':
      return '#e53935';
    default:
      return '#757575';
  }
};
// Component to update map zoom when prop changes
const MapZoom = ({
  zoom
}: {
  zoom: number;
}) => {
  const map = useMap();
  useEffect(() => {
    map.setZoom(zoom);
  }, [zoom, map]);
  return null;
};
interface SupplyChainMapProps {
  suppliers: SupplierData[];
  selectedSupplier: SupplierData | null;
  onSupplierSelect: (supplier: SupplierData | null) => void;
  filters: any;
  zoom: number;
}
const SupplyChainMap: React.FC<SupplyChainMapProps> = ({
  suppliers,
  selectedSupplier,
  onSupplierSelect,
  filters,
  zoom
}) => {
  const [filteredSuppliers, setFilteredSuppliers] = useState<SupplierData[]>(suppliers);
  const [connections, setConnections] = useState<any[]>([]);
  const [hazards, setHazards] = useState<HazardData[]>([]);
  const [filteredHazards, setFilteredHazards] = useState<HazardData[]>([]);
  const [selectedHazard, setSelectedHazard] = useState<HazardData | null>(null);
  const [clusters, setClusters] = useState<any[]>([]);
  // Import hazard data
  useEffect(() => {
    // In a real application, this would be fetched from an API
    import('../data/mockHazardsData').then(module => {
      setHazards(module.mockHazardsData);
      setFilteredHazards(module.mockHazardsData);
    });
  }, []);
  // Fix for Leaflet marker icons - moved inside component
  useEffect(() => {
    // This is needed for the marker icons to show properly
    delete (L.Icon.Default.prototype as any)._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
    });
  }, []);
  // Apply filters to suppliers
  useEffect(() => {
    let result = [...suppliers];
    if (filters.tier !== 'all') {
      result = result.filter(s => s.tier === parseInt(filters.tier));
    }
    if (filters.category !== 'all') {
      result = result.filter(s => s.category === filters.category);
    }
    if (filters.region !== 'all') {
      result = result.filter(s => s.region === filters.region);
    }
    if (filters.status !== 'all') {
      result = result.filter(s => s.status === filters.status);
    }
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      result = result.filter(s => s.name.toLowerCase().includes(query) || s.id.toLowerCase().includes(query));
    }
    setFilteredSuppliers(result);
  }, [suppliers, filters]);
  // Apply filters to hazards
  useEffect(() => {
    if (hazards.length > 0 && filters.hazardTypes) {
      const filtered = hazards.filter(hazard => filters.hazardTypes.includes(hazard.type));
      setFilteredHazards(filtered);
    } else {
      setFilteredHazards(hazards);
    }
  }, [hazards, filters.hazardTypes]);
  // Generate connections between suppliers
  useEffect(() => {
    if (!selectedSupplier) {
      // Show all connections when no supplier is selected
      const allConnections = suppliers.flatMap(supplier => (supplier.connections || []).map(connId => {
        const connectedSupplier = suppliers.find(s => s.id === connId);
        if (connectedSupplier) {
          return {
            id: `${supplier.id}-${connId}`,
            from: [supplier.lat, supplier.lng],
            to: [connectedSupplier.lat, connectedSupplier.lng],
            status: supplier.status,
            highlighted: false
          };
        }
        return null;
      }).filter(Boolean));
      setConnections(allConnections);
    } else {
      // Show only connections related to the selected supplier
      const relatedConnections = [];
      // Direct connections from selected supplier
      if (selectedSupplier.connections) {
        selectedSupplier.connections.forEach(connId => {
          const connectedSupplier = suppliers.find(s => s.id === connId);
          if (connectedSupplier) {
            relatedConnections.push({
              id: `${selectedSupplier.id}-${connId}`,
              from: [selectedSupplier.lat, selectedSupplier.lng],
              to: [connectedSupplier.lat, connectedSupplier.lng],
              status: selectedSupplier.status,
              highlighted: true
            });
          }
        });
      }
      // Connections to the selected supplier
      suppliers.forEach(supplier => {
        if (supplier.connections && supplier.connections.includes(selectedSupplier.id)) {
          relatedConnections.push({
            id: `${supplier.id}-${selectedSupplier.id}`,
            from: [supplier.lat, supplier.lng],
            to: [selectedSupplier.lat, selectedSupplier.lng],
            status: supplier.status,
            highlighted: true
          });
        }
      });
      setConnections(relatedConnections);
    }
  }, [selectedSupplier, suppliers]);
  // Get line color based on status and highlight
  const getLineColor = (status: string, highlighted: boolean) => {
    if (highlighted) {
      return status === 'At Risk' ? '#e53935' : status === 'Delayed' ? '#fbc02d' : '#4caf50';
    }
    return status === 'At Risk' ? 'rgba(229, 57, 53, 0.4)' : status === 'Delayed' ? 'rgba(251, 192, 45, 0.4)' : 'rgba(76, 175, 80, 0.4)';
  };
  // Handle hazard selection
  const handleHazardSelect = (hazard: HazardData) => {
    setSelectedHazard(hazard);
  };
  // Format impact parameters for display
  const formatImpactParameter = (key: string, value: string | number) => {
    // Convert camelCase to Title Case with spaces
    const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
    return <div key={key} className="flex justify-between text-xs py-1 border-b border-gray-100" data-id="element-266">
        <span className="text-gray-600" data-id="element-267">{formattedKey}:</span>
        <span className="font-medium" data-id="element-268">{value}</span>
      </div>;
  };
  return <MapContainer center={[20, 0]} zoom={zoom} style={{
    height: '100%',
    width: '100%'
  }} zoomControl={false} data-id="element-269">
      <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-270" />
      <MapZoom zoom={zoom} data-id="element-271" />
      {/* Render connections */}
      {connections.map(connection => <Polyline key={connection.id} positions={[connection.from, connection.to]} pathOptions={{
      color: getLineColor(connection.status, connection.highlighted),
      weight: connection.highlighted ? 3 : 2,
      dashArray: connection.status === 'Delayed' ? '5, 5' : ''
    }} data-id="element-272" />)}
      {/* Render hazard affected areas */}
      {filteredHazards.map(hazard => <Circle key={`circle-${hazard.id}`} center={[hazard.lat, hazard.lng]} radius={hazard.radius * 1000} // Convert km to meters
    pathOptions={{
      color: getHazardCircleColor(hazard.severity),
      fillColor: getHazardCircleColor(hazard.severity),
      fillOpacity: 0.15,
      weight: 1
    }} data-id="element-273" />)}
      {/* Render hazard markers */}
      {filteredHazards.map(hazard => <Marker key={`marker-${hazard.id}`} position={[hazard.lat, hazard.lng]} icon={getHazardIcon(hazard.type, hazard.severity)} eventHandlers={{
      click: () => handleHazardSelect(hazard)
    }} data-id="element-274">
          <Popup className="hazard-popup" minWidth={250} data-id="element-275">
            <div className="text-sm" data-id="element-276">
              <div className="flex items-center mb-2" data-id="element-277">
                <div className={`w-3 h-3 rounded-full mr-2 ${hazard.severity === 'critical' ? 'bg-[#e53935]' : hazard.severity === 'high' ? 'bg-[#ff9800]' : hazard.severity === 'medium' ? 'bg-[#fbc02d]' : 'bg-[#4caf50]'}`} data-id="element-278"></div>
                <h3 className="font-bold text-[#333333]" data-id="element-279">{hazard.name}</h3>
              </div>
              <div className="bg-gray-50 p-2 rounded mb-2" data-id="element-280">
                <div className="flex justify-between mb-1" data-id="element-281">
                  <span className="text-xs text-gray-500" data-id="element-282">Type:</span>
                  <span className="text-xs font-medium capitalize" data-id="element-283">
                    {hazard.type} ({hazard.subtype})
                  </span>
                </div>
                <div className="flex justify-between mb-1" data-id="element-284">
                  <span className="text-xs text-gray-500" data-id="element-285">Severity:</span>
                  <span className="text-xs font-medium capitalize" data-id="element-286">
                    {hazard.severity}
                  </span>
                </div>
                <div className="flex justify-between mb-1" data-id="element-287">
                  <span className="text-xs text-gray-500" data-id="element-288">Started:</span>
                  <span className="text-xs font-medium" data-id="element-289">
                    {hazard.startDate}
                  </span>
                </div>
                {hazard.estimatedEndDate && <div className="flex justify-between" data-id="element-290">
                    <span className="text-xs text-gray-500" data-id="element-291">Est. End:</span>
                    <span className="text-xs font-medium" data-id="element-292">
                      {hazard.estimatedEndDate}
                    </span>
                  </div>}
              </div>
              <p className="text-xs mb-2" data-id="element-293">{hazard.description}</p>
              {hazard.impactParameters && <div className="mt-2 border-t border-gray-200 pt-2" data-id="element-294">
                  <h4 className="text-xs font-medium mb-1 text-gray-600" data-id="element-295">
                    Impact Parameters:
                  </h4>
                  <div className="bg-gray-50 p-2 rounded" data-id="element-296">
                    {Object.entries(hazard.impactParameters).map(([key, value]) => formatImpactParameter(key, value))}
                  </div>
                </div>}
              {hazard.affectedSuppliers && hazard.affectedSuppliers.length > 0 && <div className="mt-2 border-t border-gray-200 pt-2" data-id="element-297">
                    <h4 className="text-xs font-medium mb-1 text-gray-600" data-id="element-298">
                      Affected Suppliers: {hazard.affectedSuppliers.length}
                    </h4>
                    <div className="flex flex-wrap gap-1" data-id="element-299">
                      {hazard.affectedSuppliers.map(id => {
                const supplier = suppliers.find(s => s.id === id);
                return supplier ? <span key={id} className="inline-block px-2 py-1 bg-gray-100 text-xs rounded cursor-pointer hover:bg-gray-200" onClick={() => {
                  const supplier = suppliers.find(s => s.id === id);
                  if (supplier) onSupplierSelect(supplier);
                }} data-id="element-300">
                            {supplier.name}
                          </span> : null;
              })}
                    </div>
                  </div>}
            </div>
          </Popup>
        </Marker>)}
      {/* Render supplier markers - without clustering */}
      {filteredSuppliers.map(supplier => <Marker key={supplier.id} position={[supplier.lat, supplier.lng]} icon={getMarkerIcon(supplier.status, supplier.tier)} eventHandlers={{
      click: () => onSupplierSelect(supplier)
    }} data-id="element-301">
          <Popup data-id="element-302">
            <div className="text-sm" data-id="element-303">
              <h3 className="font-bold text-[#005a9e]" data-id="element-304">{supplier.name}</h3>
              <p data-id="element-305">Tier: {supplier.tier}</p>
              <p data-id="element-306">Category: {supplier.category}</p>
              <p className={`font-medium ${supplier.status === 'At Risk' ? 'text-[#e53935]' : supplier.status === 'Delayed' ? 'text-[#fbc02d]' : 'text-[#4caf50]'}`} data-id="element-307">
                Status: {supplier.status}
              </p>
            </div>
          </Popup>
        </Marker>)}
    </MapContainer>;
};
export default SupplyChainMap;